import Skeleton from "./Skeleton";
import SkeletonTextLine from "./SkeletonTextLine";
import SkeletonPublicationCard from "./SkeletonPublicationCard";
import { SectionTitleSkeleton } from "../ui/SectionTitle";

// The room feed renders 5 SkeletonPublicationCards while loading, so mirror it.
const FEED_ROWS = 5;

/**
 * Cold-boot silhouette of a Room discovery page (`/room/discovery/:roomId`),
 * mirroring RoomDiscoveryPage: the room info header (name + "by org" + 52px
 * logo + description + join button), the Discovery/Resources tab row, the
 * "Thematic Collections" carousel, and the "Latest News" feed. Self-contained
 * (no queries, no room theme) so it can paint before the route mounts.
 */
export default function RoomDiscoveryBootSkeleton(): React.JSX.Element {
  return (
    <div className="flex size-full flex-col overflow-hidden bg-white">
      {/* Room info header — mirrors useRoomInfoHeader's RoomInfoSection
          (pt-16 + safe-area, name/org column + 52px logo, description). */}
      <div
        className="flex flex-col gap-4 px-4 pb-6"
        style={{ paddingTop: "calc(4rem + var(--safe-area-top))" }}
      >
        <div className="flex items-center gap-2">
          <div className="flex w-full flex-col gap-1">
            {/* Room name (22/31) + "by org" (14/20). */}
            <SkeletonTextLine width="70%" fontSize={22} lineHeight={31} />
            <SkeletonTextLine width={120} fontSize={14} lineHeight={20} />
          </div>
          {/* Room logo (Avatar lg = 52px, rounded-xl). */}
          <Skeleton className="shrink-0 rounded-xl" width={52} height={52} />
        </div>
        {/* Description (2 lines, 14/20). */}
        <div className="flex flex-col">
          <SkeletonTextLine width="100%" fontSize={14} lineHeight={20} />
          <SkeletonTextLine width="55%" fontSize={14} lineHeight={20} />
        </div>
        {/* Join button (Button xs). */}
        <Skeleton className="rounded-xl" width={72} height={32} />
      </div>

      {/* Discovery / Resources tab row. */}
      <div className="flex gap-2 px-4 pb-2">
        <Skeleton className="rounded-xl" width={104} height={36} />
        <Skeleton className="rounded-xl" width={104} height={36} />
      </div>

      {/* Thematic Collections carousel. */}
      <div className="flex flex-col gap-3 py-4">
        <div className="px-5">
          <SectionTitleSkeleton width={160} />
        </div>
        <div className="flex gap-3 px-5">
          <Skeleton className="shrink-0 rounded-2xl" width="82%" height={112} />
          <Skeleton className="shrink-0 rounded-2xl" width="82%" height={112} />
        </div>
      </div>

      {/* Latest News — section title over the divided feed list. */}
      <div className="flex flex-col gap-3 border-t border-ds-slate-100 px-4 pt-6">
        <SectionTitleSkeleton width={110} />
        <div className="flex w-full flex-col divide-y divide-ds-slate-100 *:py-4 [&>*:first-child]:pt-0">
          {Array.from({ length: FEED_ROWS }).map((_, idx) => (
            <SkeletonPublicationCard showImage key={idx} showDescription />
          ))}
        </div>
      </div>
    </div>
  );
}
